// ============================================================
// SpringBootIntro.jsx
//
// A branded, responsive course content page for the
// "Introduction to Spring & Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Ecosystem breakdown, auto-configuration logic,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import SettingsInputComponentIcon from "@mui/icons-material/SettingsInputComponent";
import SpeedIcon              from "@mui/icons-material/Speed";
import LayersIcon             from "@mui/icons-material/Layers";
import StorageIcon            from "@mui/icons-material/Storage";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function InfoCard({ title, desc, icon }) {
    return (
        <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{title}</Typography>
            </Box>
            <Typography sx={{ color: BRAND.muted, fontSize: 12, lineHeight: 1.6 }}>{desc}</Typography>
        </Paper>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootIntro() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 01"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Spring <span>& Spring Boot</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Step into the Java backend ecosystem. Explore how the Spring Framework provides the foundation for enterprise apps, and how Spring Boot accelerates development through opinionated auto-configuration.
                    </Typography>
                </Box>

                {/* 1. CORE CONCEPTS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="1. The Foundation: Spring Framework" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14, lineHeight: 1.8 }}>
                        The Spring Framework is an exhaustive infrastructure support for developing Java applications. It handles the "plumbing" of enterprise software—like transaction management and dependency injection—so you can focus on application-level business logic.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <InfoCard
                                icon={<SettingsInputComponentIcon />}
                                title="Dependency Injection"
                                desc="Objects define their dependencies only through constructor arguments or properties, allowing the framework to inject them at runtime."
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InfoCard
                                icon={<StorageIcon />}
                                title="Enterprise Ready"
                                desc="Robust support for database connectivity, security, and transaction management across different data sources."
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* 2. THE BOOT ADVANTAGE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="2. The Accelerator: Spring Boot" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Spring Boot is not a replacement for Spring; it's a way to use it faster. It eliminates the complex XML or Java configuration required to start a project.
                    </Typography>
                    <Paper sx={{ p: 3, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold, mt: 0.3 }} />
                                <Box>
                                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>Opinionated Defaults</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>Uses smart defaults to auto-configure beans based on the libraries found in your classpath.</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <CheckCircleOutlineIcon sx={{ color: BRAND.gold, mt: 0.3 }} />
                                <Box>
                                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>Embedded Servers</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>No need to deploy WAR files to external servers. Tomcat or Jetty is bundled directly into your JAR.</Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* 3. MVC VS BOOT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="3. Spring Boot vs Spring MVC" />
                    <Paper sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: `1px solid ${BRAND.border}`, background: 'rgba(0,0,0,0.3)' }}>
                            <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>Conceptual Stack</Typography>
                        </Box>
                        <Box sx={{ p: 3 }}>
                            <Typography sx={{ color: BRAND.codeText, fontFamily: 'monospace', fontSize: 14, mb: 2 }}>
                                [ Spring Boot ] -- "The Wrapper" (Starters, Auto-Config, CLI)
                            </Typography>
                            <Typography sx={{ color: BRAND.white, fontFamily: 'monospace', fontSize: 14, mb: 2 }}>
                                &nbsp;&nbsp;&nbsp;&nbsp;↳ [ Spring MVC ] -- "The Web Layer" (REST, Controllers, Views)
                            </Typography>
                            <Typography sx={{ color: BRAND.muted, fontFamily: 'monospace', fontSize: 14 }}>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;↳ [ Spring Core ] -- "The Engine" (DI, AOP, Beans)
                            </Typography>
                        </Box>
                    </Paper>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* MODULE OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Differentiate between the Core Framework and Spring Boot",
                            "Understand the concept of Dependency Injection in Java",
                            "Identify the benefits of 'Opinionated' auto-configuration",
                            "Explain the utility of Embedded Servers in microservices",
                            "Recognize the role of Spring Boot Actuator for production health",
                            "Setup your first project using Spring Initializr"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> When starting a new project, use <code style={{ color: BRAND.goldBright }}>start.spring.io</code>. It's the official Spring Initializr that lets you pick your dependencies and generates the base project structure instantly.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
